import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TagInput } from '../ui/tag-input';
import { SelectWithAdd } from '../ui/select-with-add';
import { DependentSelect } from '../ui/dependent-select';
import { X, Save, Loader2, Users } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useMultipleCodelists } from '../../hooks/useCodelist';
import { api } from '../../lib/api';
import { Training, User } from '../../types';

interface TrainingFormProps {
  training?: Training;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  mode: 'create' | 'edit' | 'view';
}

const TrainingForm: React.FC<TrainingFormProps> = ({
  training,
  isOpen,
  onClose,
  onSave,
  mode
}) => {
  const [formData, setFormData] = useState<Training>({
    title: '',
    description: '',
    type: 'security_awareness',
    category: 'mandatory',
    nis2_chapter: '', // Keep for backward compatibility
    nis2_article: '',
    nis2_letter: '',
    instructor_name: '',
    instructor_email: '',
    training_date: '',
    duration_hours: 1,
    location: 'online',
    content_description: '',
    learning_objectives: [],
    completion_criteria: '',
    certificate_provided: false,
    status: 'planned',
    tags: []
  });

  const [participants, setParticipants] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  
  // Load codelists for display names
  const { getItemName } = useMultipleCodelists(['nis2_chapters', 'training_types']);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAvailableUsers();
      if (mode === 'view' && training?.id) {
        loadParticipants();
      }
    }
  }, [isOpen, mode, training?.id]);

  // Initialize form with training data when editing/viewing
  useEffect(() => {
    if (training && isOpen) {
      setFormData({
        ...training,
        learning_objectives: training.learning_objectives || [],
        tags: training.tags || []
      });
    } else if (isOpen && mode === 'create') {
      // Reset form for create mode
      setFormData({
        title: '',
        description: '',
        type: 'security_awareness',
        category: 'mandatory',
        nis2_chapter: '', // Keep for backward compatibility
        nis2_article: '',
        nis2_letter: '',
        instructor_name: '',
        instructor_email: '',
        training_date: '',
        duration_hours: 1,
        location: 'online',
        content_description: '',
        learning_objectives: [],
        completion_criteria: '',
        certificate_provided: false,
        status: 'planned',
        tags: []
      });
    }
  }, [training, isOpen, mode]);

  const loadAvailableUsers = async () => {
    try {
      setLoading(true);
      // Use the correct API endpoint structure
      const response = await api.get<{ users: User[]; pagination: any }>('/admin/users');
      console.log('Users API response for trainings:', response);
      setAvailableUsers(response.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      // Try alternative endpoint
      try {
        const altResponse = await api.getAllUsers() as { users: User[]; pagination: any };
        console.log('Alternative users API response:', altResponse);
        setAvailableUsers(altResponse.users || []);
      } catch (altError) {
        console.error('Alternative users API also failed:', altError);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async () => {
    if (!training?.id) return;
    
    try {
      const response = await api.get<{ success: boolean; data: User[] }>(`/trainings/${training.id}/participants`);
      if (response.success) {
        setParticipants(response.data);
      }
    } catch (error) {
      console.error('Failed to load participants:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mode === 'view') return;

    try {
      setSaving(true);
      
      const sanitize = (data: any) => {
        const out: any = {};
        Object.entries(data).forEach(([key, value]) => {
          if (value === '' || value === null || value === undefined) return;
          out[key] = value;
        });
        return out;
      };

      if (mode === 'create') {
        const createData = sanitize({
          title: formData.title?.trim(),
          description: formData.description?.trim(),
          type: formData.type,
          category: formData.category,
          nis2_chapter: formData.nis2_chapter?.trim(),
          instructor_name: formData.instructor_name?.trim(),
          instructor_email: formData.instructor_email?.trim(),
          training_date: formData.training_date?.trim(),
          duration_hours: formData.duration_hours,
          location: formData.location,
          max_participants: formData.max_participants,
          content_description: formData.content_description?.trim(),
          learning_objectives: formData.learning_objectives,
          completion_criteria: formData.completion_criteria?.trim(),
          certificate_provided: formData.certificate_provided,
          status: formData.status,
          tags: formData.tags
        });
        
        const response = await api.post<{ success: boolean; data: Training }>('/trainings', createData);
        if (response.success) {
          toast({
            title: "Success",
            description: "Training created successfully",
          });
          onSave();
          onClose();
        }
      } else if (mode === 'edit' && training?.id) {
        const updateData = sanitize({
          title: formData.title?.trim(),
          description: formData.description?.trim(),
          type: formData.type,
          category: formData.category,
          nis2_chapter: formData.nis2_chapter?.trim(),
          instructor_name: formData.instructor_name?.trim(),
          instructor_email: formData.instructor_email?.trim(),
          training_date: formData.training_date?.trim(),
          duration_hours: formData.duration_hours,
          location: formData.location,
          max_participants: formData.max_participants,
          content_description: formData.content_description?.trim(),
          learning_objectives: formData.learning_objectives,
          completion_criteria: formData.completion_criteria?.trim(),
          certificate_provided: formData.certificate_provided,
          status: formData.status,
          tags: formData.tags
        });
        
        const response = await api.put<{ success: boolean; data: Training }>(`/trainings/${training.id}`, updateData);
        if (response.success) {
          toast({
            title: "Success",
            description: "Training updated successfully",
          });
          onSave();
          onClose();
        }
      }
    } catch (error: any) {
      console.error('Failed to save training:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save training",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };



  const addParticipant = async (userId: number) => {
    if (!training?.id) return;
    
    try {
      const response = await api.post<{ success: boolean; data: User }>(`/trainings/${training.id}/participants`, { user_id: userId });
      if (response.success) {
        toast({
          title: "Success",
          description: "Participant added successfully",
        });
        loadParticipants();
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add participant",
        variant: "destructive",
      });
    }
  };

  const removeParticipant = async (userId: number) => {
    if (!training?.id) return;
    
    try {
      const response = await api.delete<{ success: boolean; message: string }>(`/trainings/${training.id}/participants/${userId}`);
      if (response.success) {
        toast({
          title: "Success",
          description: "Participant removed successfully",
        });
        loadParticipants();
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to remove participant",
        variant: "destructive",
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>
              {mode === 'create' ? 'Create Training' : mode === 'edit' ? 'Edit Training' : 'Training Details'}
            </CardTitle>
            <CardDescription>
              {mode === 'create' 
                ? 'Schedule a new security training session'
                : mode === 'edit' 
                ? 'Update training information and settings'
                : 'View training details and manage participants'
              }
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Training Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Security Awareness Training"
                  required
                  disabled={mode === 'view'}
                />
              </div>

              <div className="space-y-2">
                <SelectWithAdd
                  label="Training Type"
                  value={formData.type}
                  onChange={(value) => setFormData(prev => ({ ...prev, type: value as Training['type'] }))}
                  codelistType="training_types"
                  placeholder="Select training type..."
                  required={true}
                  disabled={mode === 'view'}
                  onAddItem={async (item) => {
                    await api.post('/codelists/items', {
                      codelist_type: 'training_types',
                      code: item.code,
                      name: item.name,
                      description: item.description
                    });
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as Training['category'] }))}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                  disabled={mode === 'view'}
                >
                  <option value="mandatory">Mandatory</option>
                  <option value="optional">Optional</option>
                  <option value="specialized">Specialized</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as Training['status'] }))}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                  disabled={mode === 'view'}
                >
                  <option value="planned">Planned</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detailed description of the training"
                className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm min-h-[80px]"
                disabled={mode === 'view'}
              />
            </div>

            {/* Instructor and Scheduling */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="instructor_name">Instructor Name</Label>
                <Input
                  id="instructor_name"
                  value={formData.instructor_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, instructor_name: e.target.value }))}
                  placeholder="e.g., John Smith"
                  disabled={mode === 'view'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="instructor_email">Instructor Email</Label>
                <Input
                  id="instructor_email"
                  type="email"
                  value={formData.instructor_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, instructor_email: e.target.value }))}
                  placeholder="instructor@company.com"
                  disabled={mode === 'view'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="training_date">Training Date</Label>
                <Input
                  id="training_date"
                  type="date"
                  value={formData.training_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, training_date: e.target.value }))}
                  disabled={mode === 'view'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration_hours">Duration (hours)</Label>
                <Input
                  id="duration_hours"
                  type="number"
                  step="any"
                  min="0"
                  max="999"
                  value={formData.duration_hours}
                  onChange={(e) => {
                    const normalized = e.target.value.replace(',', '.');
                    const num = parseFloat(normalized);
                    setFormData(prev => ({ ...prev, duration_hours: isNaN(num) ? 0 : Math.round(num * 100) / 100 }));
                  }}
                  placeholder="2.5"
                  disabled={mode === 'view'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <select
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value as Training['location'] }))}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                  disabled={mode === 'view'}
                >
                  <option value="online">Online</option>
                  <option value="on-site">On-site</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_participants">Max Participants</Label>
                <Input
                  id="max_participants"
                  type="number"
                  min="1"
                  max="10000"
                  value={formData.max_participants || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_participants: e.target.value ? parseInt(e.target.value) : undefined }))}
                  placeholder="50"
                  disabled={mode === 'view'}
                />
              </div>
            </div>

            {/* NIS2 Article & Letter - Full width for better readability */}
            <div className="space-y-2">
              <DependentSelect
                label="NIS2 Article & Letter"
                parentValue={formData.nis2_article || ''}
                childValue={formData.nis2_letter || ''}
                onParentChange={(value) => setFormData(prev => ({ 
                  ...prev, 
                  nis2_article: value as string,
                  nis2_letter: '' // Reset child when parent changes
                }))}
                onChildChange={(value) => setFormData(prev => ({ ...prev, nis2_letter: value as string }))}
                parentCodelistType="nis2_articles"
                childCodelistType="nis2_letters"
                placeholder="Select NIS2 Article & Letter..."
                disabled={mode === 'view'}
              />
            </div>

            {/* Content and Certificate */}
            <div className="space-y-2 flex items-end">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="certificate_provided"
                  checked={formData.certificate_provided}
                  onChange={(e) => setFormData(prev => ({ ...prev, certificate_provided: e.target.checked }))}
                  disabled={mode === 'view'}
                  className="rounded"
                />
                <Label htmlFor="certificate_provided">Certificate Provided</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content_description">Content Description</Label>
              <textarea
                id="content_description"
                value={formData.content_description}
                onChange={(e) => setFormData(prev => ({ ...prev, content_description: e.target.value }))}
                placeholder="What topics will be covered in this training?"
                className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm min-h-[80px]"
                disabled={mode === 'view'}
              />
            </div>

            <TagInput
              tags={formData.learning_objectives}
              onTagsChange={(objectives) => setFormData(prev => ({ ...prev, learning_objectives: objectives }))}
              label="Learning Objectives"
              placeholder="e.g., Identify phishing attacks"
              disabled={mode === 'view'}
            />

            <div className="space-y-2">
              <Label htmlFor="completion_criteria">Completion Criteria</Label>
              <Input
                id="completion_criteria"
                value={formData.completion_criteria}
                onChange={(e) => setFormData(prev => ({ ...prev, completion_criteria: e.target.value }))}
                placeholder="e.g., Pass quiz with 80% or higher"
                disabled={mode === 'view'}
              />
            </div>

            <TagInput
              tags={formData.tags}
              onTagsChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
              label="Tags"
              placeholder="e.g., security, mandatory"
              disabled={mode === 'view'}
            />

            {/* Participants Management (View mode only) */}
            {mode === 'view' && training?.id && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    Participants ({participants.length})
                  </h3>
                  {formData.status === 'planned' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // TODO: Implement bulk enrollment modal
                        toast({ title: "Info", description: "Bulk enrollment coming soon!" });
                      }}
                    >
                      Add Participants
                    </Button>
                  )}
                </div>

                {participants.length > 0 ? (
                  <div className="grid gap-2">
                    {participants.map((participant) => (
                      <div key={participant.id} className="flex items-center justify-between p-3 border rounded-md">
                        <div>
                          <span className="font-medium">{participant.firstName} {participant.lastName}</span>
                          <span className="text-sm text-muted-foreground ml-2">({participant.email})</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline">{participant.role}</Badge>
                          {formData.status === 'planned' && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeParticipant(participant.id)}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No participants enrolled yet</p>
                )}

                {/* Completion Stats */}
                {training.completion_stats && formData.status === 'completed' && (
                  <div className="bg-muted p-4 rounded-md">
                    <h4 className="font-medium mb-2">Completion Statistics</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Completed:</span>
                        <span className="ml-2 font-medium">
                          {training.completion_stats.completed_participants}/{training.completion_stats.total_participants}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Completion Rate:</span>
                        <span className="ml-2 font-medium">
                          {Math.round((training.completion_stats.completed_participants / training.completion_stats.total_participants) * 100)}%
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Certificates:</span>
                        <span className="ml-2 font-medium">{training.completion_stats.certificates_issued}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Current Values Display (for view mode) */}
            {mode === 'view' && training && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Training Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">Type</span>
                    <Badge variant="outline" className="ml-2">{getItemName('training_types', formData.type)}</Badge>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Category</span>
                    <Badge variant="outline" className="ml-2">{formData.category}</Badge>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Location</span>
                    <Badge variant="outline" className="ml-2">{formData.location}</Badge>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Duration</span>
                    <Badge variant="outline" className="ml-2">{formData.duration_hours}h</Badge>
                  </div>
                  {formData.nis2_chapter && (
                    <div>
                      <span className="text-sm text-muted-foreground">NIS2 Chapter</span>
                      <Badge variant="outline" className="ml-2">{getItemName('nis2_chapters', formData.nis2_chapter)}</Badge>
                    </div>
                  )}
                </div>

                {formData.learning_objectives.length > 0 && (
                  <div>
                    <span className="text-sm text-muted-foreground">Learning Objectives:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.learning_objectives.map((objective, index) => (
                        <Badge key={index} variant="secondary">{objective}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {formData.tags.length > 0 && (
                  <div>
                    <span className="text-sm text-muted-foreground">Tags:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.tags.map((tag, index) => (
                        <Badge key={index} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {mode !== 'view' && (
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Save className="h-4 w-4 mr-2" />
                  {mode === 'create' ? 'Create Training' : 'Update Training'}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TrainingForm;
